import React, { useState } from "react";
import { Row, Col, Card, DatePicker, Button, Statistic, message, Spin } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DollarOutlined,
  CarOutlined
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import moment from "moment";
import request from "helper/request";
import "./SalesReport.scss";

const { RangePicker } = DatePicker;

const breadCrumbs = [
  { text: "REPORTS", path: "/admin/reports" },
  { text: "SALES REPORT" }
];

const SalesReport = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [reportData, setReportData] = useState(null);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleSubmit = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning("Please select a date range");
      return;
    }

    setLoading(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");

      // Make API call to fetch sales report data using the request helper
      const data = await request.get(
        `/admin/reports/sales?startDate=${startDate}&endDate=${endDate}`
      );

      setReportData(data);
      message.success("Sales report generated successfully");
    } catch (error) {
      console.error("Error fetching sales report:", error);
      message.error("Failed to generate sales report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount || 0);
  };

  return (
    <div className="sales-report">
      <AdminBreadCrumb breadCrumbs={breadCrumbs} />

      <div className="sales-report-content">
        <div className="sales-report-header">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/admin/reports")}
            style={{ padding: 0, marginBottom: "16px" }}
          >
            Back to Reports
          </Button>
          <h1 style={{ color: "#1890ff", marginBottom: "8px" }}>Sales Report</h1>
          <p style={{ color: "#666", marginBottom: "32px" }}>
            Generate comprehensive sales analytics for any date range
          </p>
        </div>

        <Card className="date-selector-card" style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={16} md={18}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <CalendarOutlined style={{ color: "#1890ff", fontSize: "16px" }} />
                <span style={{ marginRight: "8px" }}>Select Date Range:</span>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  placeholder={["Start Date", "End Date"]}
                  style={{ width: "300px" }}
                  disabledDate={(current) => current && current > moment().endOf("day")}
                />
              </div>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!dateRange || dateRange.length !== 2}
                style={{ width: "100%" }}
              >
                Generate Report
              </Button>
            </Col>
          </Row>
        </Card>

        {loading && (
          <div className="loading-container">
            <Spin size="large" tip="Generating sales report..." />
          </div>
        )}

        {reportData && !loading && (
          <div className="report-results">
            <h2 style={{ color: "#1890ff", marginBottom: "24px" }}>
              Sales Report: {dateRange[0].format("MMM DD, YYYY")} -{" "}
              {dateRange[1].format("MMM DD, YYYY")}
            </h2>

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Routes"
                    value={reportData.totalRoutes}
                    prefix={<CarOutlined style={{ color: "#1890ff" }} />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Client Value"
                    value={reportData.totalClientValue}
                    prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
                    valueStyle={{ color: "#52c41a" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Driver Value"
                    value={reportData.totalDriverValue}
                    prefix={<DollarOutlined style={{ color: "#fa8c16" }} />}
                    valueStyle={{ color: "#fa8c16" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Card>
              </Col>
            </Row>

            <Card style={{ marginTop: "24px" }}>
              <h3>Summary</h3>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p>
                    <strong>Report Period:</strong>{" "}
                    {dateRange[1].diff(dateRange[0], "days") + 1} days
                  </p>
                  <p>
                    <strong>Average Routes per Day:</strong>{" "}
                    {(
                      reportData.totalRoutes /
                      (dateRange[1].diff(dateRange[0], "days") + 1)
                    ).toFixed(1)}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Average Client Value per Route:</strong>{" "}
                    {formatCurrency(
                      reportData.totalClientValue / (reportData.totalRoutes || 1)
                    )}
                  </p>
                  <p>
                    <strong>Average Driver Value per Route:</strong>{" "}
                    {formatCurrency(
                      reportData.totalDriverValue / (reportData.totalRoutes || 1)
                    )}
                  </p>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        {!reportData && !loading && (
          <Card className="empty-state">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <CalendarOutlined
                style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }}
              />
              <h3 style={{ color: "#666" }}>No Report Generated</h3>
              <p style={{ color: "#999" }}>
                Select a date range and click Generate Report to view sales analytics
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SalesReport;
